import { handleUnauthorizedError, useAuth } from '@/lib/auth';
import { QueryClient } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { LogService } from './log/log.service';

declare module 'axios' {
  export interface AxiosRequestConfig {
    onError?: Function;
    onSuccess?: Function;
    options?: {
      ignoreAbortController?: boolean;
      isLogRequest?: boolean;
    };
    id?: string;
  }
}

const axiosInstance = axios.create();
axiosInstance.defaults.headers.common['Content-Type'] = 'application/json';
// axiosInstance.defaults.headers.common['ngrok-skip-browser-warning'] = true;
axiosInstance.defaults.withCredentials = true;

//? Contains all the request ids and abort controllers related with them
export const requestAbortControllers: any = {};

export const abortRequest = (requestId: string) => {
  if (requestAbortControllers[requestId]) {
    requestAbortControllers[requestId]?.abort();
  }
};

const getAuthToken = () => {
  const token = localStorage.getItem('jwt'); //? Change this to wherever we store the bearer token, if we are saving it in authState we could grab it from the authState localStorage item
  const key = localStorage.getItem('public_key');
  return {
    token: token,
    key: key
  };
};

// Add these at the top with other imports
let lastLogTime = 0;
const LOG_THROTTLE_MS = 1000; // Only allow one log per second

function logData(res: AxiosResponse) {
  const { method, url } = res?.config;
  const logData = {
    type: 'success',
    method: method?.toUpperCase(),
    url: url,
    status: res.status,
    statusText: res.statusText,
    timestamp: new Date().toISOString(),
    requestHeaders: res.config.headers,
    responseHeaders: res.headers,
    requestData: res.config.data,
    responseSize: JSON.stringify(res.data).length,
    maxRate: res.config?.maxRate,
    queryParams: res.config.params,
    stack: new Error().stack?.split('\n').slice(1).map(line => line.trim()),
    userAgent: navigator.userAgent,
    environment: import.meta.env.MODE,
    // Include a subset of response data for debugging
    // Be careful not to log sensitive data
    // responsePreview: JSON.stringify(res.data).slice(0, 500) + (JSON.stringify(res.data).length > 500 ? '...' : '')
  };
  LogService.json(logData);
}

function logError(err: any) {

  const { config, response } = err;
  const errorData = {
    type: 'error',
    method: config?.method?.toUpperCase(),
    url: config?.url,
    status: response?.status,
    statusText: response?.statusText,
    timestamp: new Date().toISOString(),
    requestHeaders: config?.headers,
    responseHeaders: response?.headers,
    requestData: config?.data,
    responseSize: response?.data ? JSON.stringify(response.data).length : 0,
    maxRate: config?.maxRate,
    queryParams: config?.params,
    stack: new Error().stack?.split('\n').slice(1).map(line => line.trim()),
    userAgent: navigator.userAgent,
    environment: import.meta.env.MODE,
    message: err.message,
    responsePreview: response?.data ? JSON.stringify(response.data).slice(0, 500) + (JSON.stringify(response.data).length > 500 ? '...' : '') : undefined
  }

  LogService.json(errorData);

}

//? Can pass clearAuth to this function and automatically call it in the interceptors.response error to log the user out and clear the global auth state
//? Add this to app.tsx or header or something that could pass in the clearAuth in the future
export const setupInterceptors = (queryClient: QueryClient) => {
  // const auth = useAuth();
  axiosInstance.interceptors.request.use(
    async (config: any) => {
      const requestAbortId = `controller/${config.url}_${config.id}`;
      if (!config?.options?.ignoreAbortController && requestAbortControllers[requestAbortId]) {
        //? This will abort the request if the same request is called more than once.
        abortRequest(requestAbortId);
      }
      if (!config.headers) {
        config.headers = { 'ngrok-skip-browser-warning': true };
      }
      const { token, key } = await getAuthToken();
      config.headers['Authorization'] = token
      config.headers['PublicKey'] = key
      requestAbortControllers[requestAbortId] = new AbortController();
      if (requestAbortControllers[requestAbortId] !== undefined) {
        config.signal = requestAbortControllers[requestAbortId]?.signal;
      }
      return config;
    },
    (error: AxiosError) => {
      //? Add custom error logic here
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (res: AxiosResponse) => {
      // Log successful requests asynchronously with a throttle
      if (res?.config?.options?.isLogRequest) return res; // Skip logging for log requests
      if (res?.status === 200) {
        // console.log(res)
        logData(res)
      } else {
        logError(res)
      }

      // return res;
      // const now = Date.now();
      // if (now - lastLogTime >= LOG_THROTTLE_MS) {
      //   lastLogTime = now;
      // const logService = LogService();
      // setTimeout(() => {
      //   try {
      //     logService.info('hello world').catch(() => {
      //       // Silently fail logging - don't impact main application
      //     });
      //   } catch (e) {
      //     // Ignore logging errors
      //   }
      //   }, 0);
      // }

      if (res?.config?.onSuccess) {
        return res?.config?.onSuccess(res);
      }
      return res;

    },
    (err: AxiosError) => {
      //? Can add code here to check the authorization token expired date and automatically log the user out
      // Log errors asynchronously with a throttle
      if (err.config?.options?.isLogRequest) return Promise.reject(err); // Skip logging

      logError(err)
      if (err?.config?.onError) {
        return err?.config?.onError(err);
      }
      return err;
      // return Promise.reject(err);
      // const now = Date.now();
      // if (now - lastLogTime >= LOG_THROTTLE_MS) {
      //   lastLogTime = now;
      // const logService = LogService();
      // setTimeout(() => {
      //   try {
      //     logService.info(JSON.stringify({
      //       type: 'error',
      //       status: err.response?.status,
      //       method: err.config?.method,
      //       url: err.config?.url,
      //       error: err.message,
      //       message: err.config?.data,
      //       timestamp: new Date().toISOString(),
      //     })).catch(() => {
      //       // Silently fail logging - don't impact main application
      //     });
      //   } catch (e) {
      //     // Ignore logging errors
      //   }
      //   }, 0);
      // }



    },
  );
};

// setupInterceptors();

export const apiService = axiosInstance;
